import { useState } from 'react';
import api from '../../../api/client';

export const useInterviewResults = () => {
    // ViewResult Modal State
    const [showFeedbackModal, setShowFeedbackModal] = useState(false);
    const [feedback, setFeedback] = useState(null);
    const [loadingFeedback, setLoadingFeedback] = useState(false);
    const [feedbackError, setFeedbackError] = useState('');
    const [sessionData, setSessionData] = useState(null);

    // Email functionality state
    const [isSendingEmail, setIsSendingEmail] = useState(false);
    const [emailSent, setEmailSent] = useState(false);
    const [showEmailConfirmModal, setShowEmailConfirmModal] = useState(false);
    const [emailRecipient, setEmailRecipient] = useState(null);
    const [ccRecipients, setCcRecipients] = useState('');

    const getAuthHeaders = () => {
        const rawToken = localStorage.getItem('adminToken') || localStorage.getItem('token');
        if (!rawToken) return {};
        if (rawToken.startsWith('Token ') || rawToken.startsWith('Bearer ')) {
            return { Authorization: rawToken };
        }
        if (rawToken.split('.').length === 3) {
            return { Authorization: `Bearer ${rawToken}` };
        }
        return { Authorization: `Token ${rawToken}` };
    };

    // Fetch interview feedback and session data
    const fetchInterviewResults = async (sessionId) => {
        setLoadingFeedback(true);
        setFeedbackError('');
        setFeedback(null);
        setSessionData(null);

        try {
            const authHeaders = getAuthHeaders();

            // Fetch session data
            const sessionRes = await api.get(`sessions/${sessionId}/`, {
                headers: authHeaders,
            });
            setSessionData(sessionRes.data);

            // Fetch feedback data
            const feedbackRes = await api.get(`feedback/?session=${sessionId}`, {
                headers: authHeaders,
            });

            if (!feedbackRes.data || !feedbackRes.data.results || feedbackRes.data.results.length === 0) {
                setFeedbackError('No feedback found for this interview session.');
                return { success: false, error: 'No feedback found for this interview session.' };
            }

            const feedbackData = feedbackRes.data.results[0];
            setFeedback(feedbackData);
            return { success: true, feedback: feedbackData, sessionData: sessionRes.data };

        } catch (err) {
            console.error('Error loading interview results:', err);
            const errorMessage = 'Failed to load interview results. Please try again.';
            setFeedbackError(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            setLoadingFeedback(false);
        }
    };

    // Send feedback email
    const sendFeedbackEmail = async (emailData) => {
        setIsSendingEmail(true);
        try {
            await api.post('admin/send-feedback-email/', emailData, {
                headers: getAuthHeaders(),
            });

            setEmailSent(true);
            return { success: true, message: 'Interview feedback email sent successfully!' };

        } catch (error) {
            console.error('Error sending email:', error);
            const errorMessage = error.response?.data?.error || 'Failed to send feedback email';
            return { success: false, error: errorMessage };
        } finally {
            setIsSendingEmail(false);
        }
    };

    // Open feedback modal
    const openFeedbackModal = () => {
        setShowFeedbackModal(true);
    };

    // Close feedback modal
    const closeFeedbackModal = () => {
        setShowFeedbackModal(false);
        setFeedback(null);
        setSessionData(null);
        setFeedbackError('');
    };

    // Prepare email data
    const prepareEmailSend = (candidateName, candidateEmail, htmlContent = null, filename = null, compactMode = false) => {
        setEmailRecipient({
            name: candidateName,
            email: candidateEmail,
            htmlContent: htmlContent,
            filename: filename,
            compactMode: compactMode
        });
        setShowEmailConfirmModal(true);
        setShowFeedbackModal(false);
    };

    // Close email modal
    const closeEmailModal = () => {
        setShowEmailConfirmModal(false);
        setCcRecipients('');
        setEmailRecipient(null);
        setEmailSent(false);
    };

    // Reset all states
    const resetAllStates = () => {
        closeFeedbackModal();
        closeEmailModal();
    };

    return {
        // State
        showFeedbackModal,
        feedback,
        loadingFeedback,
        feedbackError,
        sessionData,
        isSendingEmail,
        emailSent,
        showEmailConfirmModal,
        emailRecipient,
        ccRecipients,

        // State setters
        setCcRecipients,
        setEmailRecipient,

        // API functions
        fetchInterviewResults,
        sendFeedbackEmail,

        // Modal controls
        openFeedbackModal,
        closeFeedbackModal,
        prepareEmailSend,
        closeEmailModal,
        resetAllStates
    };
};

export default useInterviewResults;